/* eslint-disable jsx-a11y/anchor-is-valid */

/* eslint-disable react/no-unknown-property */
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Assignment, LocalAtm, Payment } from '@mui/icons-material';
import { Box, Card, Divider, ListItem, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { FuseIcon } from '/src/components';
import { useStatementStore } from '/src/context';
import { mediumDate, shortDate } from '/src/lib/constants';
import { TLenderId } from '/src/lib/models';
import { DataModelPluralType, DataModelType } from '/src/lib/types';
import { currency } from '/src/utils';

export const ViewLenderStatementPage = observer(() => {
  const rowHeight = 48;

  /** Context **/
  const statementStore = useStatementStore();
  const params = useParams();
  const navigate = useNavigate();

  /** Computed **/
  const lenderId = params.lenderId;
  const month = params.month;

  const statementDate = dayjs(month, 'YYYY-MM');
  const startDate = statementDate.startOf('month');
  const endDate = statementDate.endOf('month');

  const headlines = statementStore.headlines;
  const totals = statementStore.totals;
  const loans = statementStore.loans;
  const lender = statementStore.currentLender;

  /** Methods **/

  /**
   * Query for LenderStatementTransactions and prepare the data
   */
  const getTxns = async (lenderId: TLenderId, month: string) => {
    // Make sure the requested statement date is in the past
    if (dayjs(month, 'YYYY-MM').isBefore(dayjs())) {
      await statementStore.getLenderStatement(lenderId, month);
    } else {
      navigate('/statements');
    }
  };

  /** Effects **/

  useEffect(() => {
    if (lenderId && month) getTxns(lenderId, month);
  }, [lenderId, month]);

  /** Render **/
  return (
    <section className="content">
      <section className="container">
        <Box className="margin-sides-lg pad-bottom md-default-theme">
          <Box display="flex" justifyContent="start" alignItems="top" className="margin-top-md pad-sides-xs">
            <Box display="flex" justifyContent="start" alignItems="top" className="pad-top-sm">
              <Box>
                <FuseIcon className="margin-right-xs margin-bottom-xs logo-size" />
              </Box>

              <Box className="thin text-size-md">
                <Box component="span" className="medium">
                  FuSE Financial
                </Box>{' '}
                | 801 K Street, Suite 2700 | Sacramento, CA 95814
                <Divider />
              </Box>
            </Box>

            <Box component="span" display="flex" flex={1} />

            <Box flexDirection="column" display="flex" justifyContent="center" alignItems="end">
              <Box component="span" className="md-headline thin">
                Investor Activity Statement
              </Box>
              <Box component="span" className="md-title-print">
                {startDate?.format('MMMM YYYY')}
              </Box>
            </Box>
          </Box>

          <Box display="flex" justifyContent="top" alignItems="top" className="margin-top-lg print-margin-top">
            <Box
              flexDirection="column"
              display="flex"
              justifyContent="top"
              alignItems="top"
              flex={33}
              className="thin pad-left-sm"
            >
              <Box component="span" className="md-headline">
                {lender?.longName}
              </Box>
              <Box component="span" className="text-sm">
                {lender?.address?.street}
              </Box>
              <Box component="span" className="text-sm">
                {lender?.address?.city}, {lender?.address?.state}, {lender?.address?.zip}
              </Box>
            </Box>

            <Box
              flexDirection="column"
              display="flex"
              flex={66}
              justifyContent="center"
              alignItems="top"
              className="pad-sides-xs loan-summary-text"
            >
              <ListItem className="height-small text-size-md" sx={{ height: '35px' }}>
                <Box width="100%" component="span" />
                <Box width="100%" component="span" className="text-right">
                  Month
                </Box>
                <Box width="100%" component="span" className="text-right">
                  YTD
                </Box>
                <Box width="100%" className="text-right" component="span">
                  {/* <Box width="100%" className="text-right"> */}
                  All-Time
                  {/* </Box> */}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small medium" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Amount Invested
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.month?.account?.amountInvested ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.ytd.account?.amountInvested ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.alltime.account?.amountInvested ?? 0)}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Current Principal Balances
                </Box>
                <Box width="100%" component="span" />
                <Box width="100%" component="span" />
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.alltime.principalBalance ?? 0)}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Fees Collected
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.month?.appliedToFees ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.ytd.appliedToFees ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.alltime.appliedToFees ?? 0)}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Interest Collected
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.month?.appliedToInterest ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.ytd.appliedToInterest ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.alltime.appliedToInterest ?? 0)}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Total Revenue
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.month?.totalRevenue ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.ytd.totalRevenue ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.alltime.totalRevenue ?? 0)}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Socket Fees
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.month?.managementFees ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.ytd.managementFees ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.alltime.managementFees ?? 0)}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small medium" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Net Revenue Collected
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.month?.netRevenue ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.ytd.netRevenue ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.alltime.netRevenue ?? 0)}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Returned to Investor
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.month?.account?.amountReturned ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.ytd.account?.amountReturned ?? 0)}
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.alltime.account?.amountReturned ?? 0)}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small medium" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Holding Account Balance
                </Box>
                <Box width="100%" component="span" />
                <Box width="100%" component="span" />
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.alltime.balances ?? 0)}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Accrued Unpaid Deal Fees
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.month?.feeBalance ?? 0)}
                </Box>
                <Box width="100%" component="span" />
                <Box width="100%" component="span" />
              </ListItem>

              <Divider />

              <ListItem className="height-small" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Accrued Unpaid Deal Interest
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.month?.interestBalance ?? 0)}
                </Box>
                <Box width="100%" component="span" />
                <Box width="100%" component="span" />
              </ListItem>

              <Divider />

              <ListItem className="height-small medium" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Total Accrued Unpaid Revenue
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.month?.totalUnpaidRevenue ?? 0)}
                </Box>
                <Box width="100%" component="span" />
                <Box width="100%" component="span" />
              </ListItem>
            </Box>
          </Box>

          <Box
            display="flex"
            flex={1}
            justifyContent="center"
            alignItems="center"
            className="margin-top-lg print-margin-top"
          >
            <Box display="flex" flex={33}>
              <Card component="md-card" style={{ background: 'white', display: 'flex', flex: 100 }}>
                <Box
                  className="toolbar-div-print text-size-sm"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box component="span" display="flex" />
                  <strong>Monthly Deal Balance Activity</strong>
                  <Box component="span" display="flex" />
                </Box>

                <Box className="statement-list-text">
                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Begin Principal Balances
                    <Box component="span" display="flex" flex={1} />
                    {currency.format(statementStore.totalBeginPrincipalBalance ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Advances
                    <Box component="span" display="flex" flex={1} />
                    {currency.format(totals?.advanceAmount ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Principal Payments
                    <Box component="span" display="flex" flex={1} />
                    {currency.format(totals?.principalPaid ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Ending Principal Balances
                    <Box component="span" display="flex" flex={1} />
                    {currency.format(statementStore.totalEndingPrincipalBalance ?? 0)}
                  </ListItem>
                </Box>
              </Card>
            </Box>

            <Box display="flex" flex={33}>
              <Card component="md-card" style={{ background: 'white', display: 'flex', flex: 100 }}>
                <Box
                  className="toolbar-div-print text-size-sm"
                  display="flex"
                  flex={1}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box component="span" display="flex" flex={1} />
                  <strong>Monthly Payment Activity</strong>
                  <Box component="span" display="flex" flex={1} />
                </Box>

                <Box className="statement-list-text">
                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Total Payments
                    <Box component="span" display="flex" flex={1} />
                    {currency.format(totals?.paymentAmount ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Fees Paid
                    <Box component="span" display="flex" flex={1} />
                    {currency.format(totals?.feesPaid ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Interest Paid
                    <Box component="span" display="flex" flex={1} />
                    {currency.format(totals?.interestPaid ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Principal Paid
                    <Box component="span" display="flex" flex={1} />
                    {currency.format(totals?.principalPaid ?? 0)}
                  </ListItem>
                </Box>
              </Card>
            </Box>

            <Box display="flex" flex={33}>
              <Card component="md-card" style={{ background: 'white', display: 'flex', flex: 100 }}>
                <Box
                  className="toolbar-div-print text-size-sm"
                  display="flex"
                  flex={1}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box component="span" display="flex" flex={1} />
                  <strong>Month-End Asset Summary</strong>
                  <Box component="span" display="flex" flex={1} />
                </Box>

                <Box className="statement-list-text">
                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Asset Value
                    <Box component="span" display="flex" flex={1} />
                    {currency.format(headlines?.month?.assetValue)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Total Available Credit
                    <Box component="span" display="flex" flex={1} />
                    {currency.format(headlines?.month?.totalAvailableCredit)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Principal Balance
                    <Box component="span" display="flex" flex={1} />
                    {currency.format(headlines?.month?.principalBalance)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Current Available Credit
                    <Box component="span" display="flex" flex={1} />
                    {currency.format(headlines?.month?.currentAvailableCredit)}
                  </ListItem>
                </Box>
              </Card>
            </Box>
          </Box>

          {!true &&
            loans
              ?.toSorted((a, b) => (a.name > b.name ? 1 : -1))
              .map((loan) => (
                <Card key={loan.name} className="margin-top-md">
                  <Box
                    className="toolbar-div-print text-size-sm"
                    display="flex"
                    flex={1}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Box component="span" display="flex" flex={1} />
                    {loan.name} - Transaction Activity {startDate.format(shortDate)} -{' '}
                    {dayjs(endDate).format(shortDate)}
                    <Box component="span" display="flex" flex={1} />
                  </Box>

                  <Divider />

                  <Box className="fadeDown">
                    {/* TRANSACTIONS LIST HEADER */}
                    {
                      <ListItem className="">
                        <Box
                          display="flex"
                          flex={1}
                          justifyContent="space-around"
                          alignItems="center"
                          className="medium md-caption text-center"
                        >
                          <Box component="span" display="flex" flex={1}>
                            Type
                          </Box>
                          <Box component="span" display="flex" flex={1}>
                            Effective Date
                          </Box>
                          <Box component="span" display="flex" flex={1} className="text-right">
                            Advance Amount
                          </Box>
                          <Box component="span" display="flex" flex={1} className="text-right">
                            Fees Charged
                          </Box>
                          <Box component="span" display="flex" flex={1} className="text-right">
                            Payment Amount
                          </Box>
                          <Box component="span" display="flex" flex={1} className="text-right">
                            Fees Paid
                          </Box>
                          <Box component="span" display="flex" flex={1} className="text-right">
                            Interest Paid
                          </Box>
                          <Box component="span" display="flex" flex={1} className="text-right">
                            Principal Paid
                          </Box>
                          <Box component="span" display="flex" flex={1} className="text-right">
                            Principal Balance
                          </Box>
                        </Box>
                      </ListItem>
                    }

                    {/* TRANSACTIONS */}
                    <Box className="md-content">
                      <Divider />

                      {loan.loanTxns
                        .filter((transaction) => transaction.__t !== DataModelPluralType.Adjustment)
                        .map((transaction, i) => (
                          <ListItem
                            key={transaction._id}
                            // ng-repeat="transaction in transactionHistory | orderBy:sortData:sortReverse"
                            className={`capitalize row-height-48 ${i % 2 ? 'highlighted' : ''}`}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-around"
                              alignItems="center"
                              className="text-center md-subhead-sm-print"
                              flex="100"
                            >
                              {/* TYPE */}
                              <Box display="flex" flex={1}>
                                {transaction.__t === DataModelPluralType.Advance && (
                                  <Box component="span" display="flex" flex={1}>
                                    <Tooltip title={DataModelType.Advance}>
                                      <Payment className="md-icon material-icons" sx={{ fontSize: '1.9em' }} />
                                    </Tooltip>
                                  </Box>
                                )}
                                {transaction.__t === DataModelPluralType.Payment && (
                                  <Box component="span" display="flex" flex={1}>
                                    <Tooltip title={DataModelType.Payment}>
                                      <LocalAtm className="md-icon material-icons" sx={{ fontSize: '1.9em' }} />
                                    </Tooltip>
                                  </Box>
                                )}
                                {transaction.__t === DataModelPluralType.Statement && (
                                  <Box component="span" display="flex" flex={1}>
                                    <Tooltip title={DataModelType.Statement}>
                                      <Assignment className="md-icon material-icons" sx={{ fontSize: '1.9em' }} />
                                    </Tooltip>
                                  </Box>
                                )}
                              </Box>

                              {/* EFFECTIVE DATE */}
                              <Box component="span" display="flex" flex={1}>
                                {dayjs(transaction.effectiveDate).format(mediumDate)}
                              </Box>

                              {/* ADVANCE AMOUNT */}
                              <Box component="span" display="flex" flex={1} className="text-right">
                                <a ng-href="#!/{{transaction.__t}/{{transaction._id}}">
                                  {transaction.__t === DataModelPluralType.Advance &&
                                    currency.format(transaction.advanceAmount ?? 0)}
                                </a>
                              </Box>

                              {/* FEES CHARGED */}
                              <Box component="span" display="flex" flex={1} className="text-right">
                                <a
                                  ng-hide="transaction.__t == DataModelPluralType.Statement"
                                  ng-href="#!/{{transaction.__t}/{{transaction._id}}"
                                >
                                  {transaction.__t !== DataModelPluralType.Statement &&
                                    currency.format(transaction.fees ?? 0)}
                                </a>
                              </Box>

                              {/* PAYMENT AMOUNT */}
                              <Box component="span" display="flex" flex={1} className="text-right">
                                <a
                                  ng-hide="transaction.__t == DataModelPluralType.Statement"
                                  ng-href="#!/{{transaction.__t}/{{transaction._id}}"
                                >
                                  {transaction.__t === DataModelPluralType.Payment &&
                                    currency.format(transaction.paymentAmount ?? 0)}
                                </a>
                              </Box>

                              {/* FEES PAID */}
                              <Box component="span" display="flex" flex={1} className="text-right">
                                {transaction.__t === DataModelPluralType.Payment &&
                                  currency.format(transaction.appliedToFees ?? 0)}
                              </Box>

                              {/* INTEREST PAID */}
                              <Box component="span" display="flex" flex={1} className="text-right">
                                {transaction.__t === DataModelPluralType.Payment &&
                                  currency.format(transaction.appliedToInterest ?? 0)}
                              </Box>

                              {/* INTEREST DUE */}
                              <Box component="span" display="flex" flex={1} className="text-right">
                                {transaction.__t === DataModelPluralType.Payment &&
                                  currency.format(transaction.appliedToPrincipal ?? 0)}
                              </Box>

                              <Box component="span" className="divider-bar" />

                              {/* PRINCIPAL BALANCE */}
                              <Box component="span" display="flex" flex={1} className="text-right">
                                {currency.format(
                                  (transaction.__t === DataModelPluralType.Statement
                                    ? transaction.principalBalance
                                    : transaction.newPrincipalBalance) ?? 0
                                )}
                              </Box>
                            </Box>
                            <Divider />
                          </ListItem>
                        ))}

                      {/* TOTALS */}
                      {!!loan.loanTxns.length && !statementStore.allAdjustments && <Divider />}
                      {!!loan.loanTxns.length && !statementStore.allAdjustments && (
                        <ListItem>
                          <Box
                            display="flex"
                            justifyContent="space-around"
                            alignItems="center"
                            className="medium md-subhead-sm text-center"
                            flex="100"
                          >
                            <Box component="span" display="flex" flex={1}>
                              Totals
                            </Box>
                            <Box component="span" display="flex" flex={1} />

                            {/* TOTAL ADVANCE AMOUNTS */}
                            <Box component="span" display="flex" flex={1} className="text-right">
                              {currency.format(loan.totals.advanceAmount)}
                            </Box>

                            {/* TOTAL FEES CHARGED */}
                            <Box component="span" display="flex" flex={1} className="text-right">
                              {currency.format(loan.totals.feesCharged)}
                            </Box>

                            {/* TOTAL PAYMENT AMOUNTS */}
                            <Box component="span" display="flex" flex={1} className="text-right">
                              {currency.format(loan.totals.paymentAmount)}
                            </Box>

                            {/* TOTAL FEES PAID */}
                            <Box component="span" display="flex" flex={1} className="text-right">
                              {currency.format(loan.totals.feesPaid)}
                            </Box>

                            {/* TOTAL INTEREST PAID */}
                            <Box component="span" display="flex" flex={1} className="text-right">
                              {currency.format(loan.totals.interestPaid)}
                            </Box>

                            {/* TOTAL PRINCIPAL PAID */}
                            <Box component="span" display="flex" flex={1} className="text-right">
                              {currency.format(loan.totals.principalPaid)}
                            </Box>

                            <Box component="span" display="flex" flex={1} />
                          </Box>
                        </ListItem>
                      )}
                    </Box>

                    {!loan.loanTxns.length && (
                      <ListItem sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Box component="span" display="flex" flex={1} />
                        <Box className="md-warn"> No transactions were recorded for this period.</Box>
                        <Box component="span" display="flex" flex={1} />
                      </ListItem>
                    )}
                  </Box>
                </Card>
              ))}
        </Box>
      </section>
    </section>
  );
});
